/**
 * The template editor's item model (plan docs/plans/lms-plan.md 12.1, 5.7,
 * 18.24; services/lms/onboarding/validators.js parseTemplateItem; WP-47; R14).
 *
 * The editor holds the whole ordered list of a template's items as drafts
 * (field text as typed) and sends it back with PUT .../items: ids kept for the
 * items that exist, the list order is the ordinal, an existing item left out
 * is retired by the server (never deleted), and a retired item listed again
 * comes back. The rules here are the server's, checked first so a mistake is
 * shown next to its field:
 *   - a title of 1 to 200 characters; a description of at most 5000;
 *   - due days a whole number from 0 to 365 (5.7 default 14);
 *   - a link that is a full http or https address of at most 2000 characters;
 *   - a course item names its course and is the new team member's (12.2
 *     assigns the course to them);
 *   - only a course or an external training links a course (12.1).
 */
import {
  COURSE_LINK_KINDS,
  ONBOARDING_ITEM_DEFAULTS,
  ONBOARDING_LIMITS,
} from '../../../api/onboarding';
import type {
  OnboardingEvidence,
  OnboardingItemKind,
  OnboardingResponsible,
  OnboardingTemplateItem,
  OnboardingTemplateItemInput,
} from '../../../api/onboarding';

export interface DraftItem {
  /** A stable key for React and the test ids: the item's id, or new-<n> until it is saved. */
  key: string;
  id: string | null;
  title: string;
  description_md: string;
  kind: OnboardingItemKind;
  url: string;
  responsible: OnboardingResponsible;
  evidence: OnboardingEvidence;
  /** As typed. */
  due_days: string;
  course_id: string | null;
  requires_verification: boolean;
}

export type DraftField = 'title' | 'description_md' | 'url' | 'due_days' | 'course_id' | 'responsible';

export type DraftProblems = Partial<Record<DraftField, string>>;

export function draftOf(item: OnboardingTemplateItem): DraftItem {
  return {
    key: item.id,
    id: item.id,
    title: item.title ?? '',
    description_md: item.description_md ?? '',
    kind: item.kind,
    url: item.url ?? '',
    responsible: item.responsible,
    evidence: item.evidence ?? ONBOARDING_ITEM_DEFAULTS.evidence,
    due_days: String(item.due_days ?? ONBOARDING_ITEM_DEFAULTS.due_days),
    course_id: item.course_id ?? null,
    requires_verification: item.requires_verification === true,
  };
}

let nextNew = 0;

/** A new item with 5.7's defaults (evidence none, due in 14 days, no verification). */
export function newDraft(): DraftItem {
  nextNew += 1;
  return {
    key: `new-${nextNew}`,
    id: null,
    title: '',
    description_md: '',
    kind: 'custom',
    url: '',
    responsible: 'new_hire',
    evidence: ONBOARDING_ITEM_DEFAULTS.evidence,
    due_days: String(ONBOARDING_ITEM_DEFAULTS.due_days),
    course_id: null,
    requires_verification: ONBOARDING_ITEM_DEFAULTS.requires_verification,
  };
}

/** Change an item's kind with the rules that follow from it (12.1, 12.2). */
export function withKind(draft: DraftItem, kind: OnboardingItemKind): DraftItem {
  const next: DraftItem = { ...draft, kind };
  if (!COURSE_LINK_KINDS.includes(kind)) next.course_id = null;
  if (kind === 'course') next.responsible = 'new_hire';
  return next;
}

/** A whole number of days from 0 to 365, or null. */
export function parseDueDays(text: string): number | null {
  const t = text.trim();
  if (!/^[0-9]+$/.test(t)) return null;
  const n = Number(t);
  return Number.isInteger(n) && n >= 0 && n <= ONBOARDING_LIMITS.dueDaysMax ? n : null;
}

/** The server's item rules, per field (validators.js parseTemplateItem). */
export function itemProblems(d: DraftItem): DraftProblems {
  const p: DraftProblems = {};
  const title = d.title.trim();
  if (!title) p.title = 'Give the item a title.';
  else if (title.length > ONBOARDING_LIMITS.itemTitle) p.title = `A title is at most ${ONBOARDING_LIMITS.itemTitle} characters.`;
  if (d.description_md.trim().length > ONBOARDING_LIMITS.itemDescription) {
    p.description_md = `A description is at most ${ONBOARDING_LIMITS.itemDescription} characters.`;
  }
  if (parseDueDays(d.due_days) === null) p.due_days = `A whole number of days from 0 to ${ONBOARDING_LIMITS.dueDaysMax}.`;
  const url = d.url.trim();
  if (url) {
    if (!/^https?:\/\/\S+$/i.test(url)) p.url = 'A full address starting with http:// or https://.';
    else if (url.length > ONBOARDING_LIMITS.url) p.url = `A link is at most ${ONBOARDING_LIMITS.url} characters.`;
  }
  if (d.kind === 'course' && !d.course_id) p.course_id = 'Choose the course this item completes with.';
  if (d.course_id && !COURSE_LINK_KINDS.includes(d.kind)) p.course_id = 'Only a course or an external training links a course.';
  if (d.kind === 'course' && d.responsible !== 'new_hire') p.responsible = "A course item is the new team member's.";
  return p;
}

export function hasProblems(p: DraftProblems): boolean {
  return Object.keys(p).length > 0;
}

/** One entry of the PUT's list (only ever called on a draft without problems). */
export function toInput(d: DraftItem): OnboardingTemplateItemInput {
  const input: OnboardingTemplateItemInput = {
    title: d.title.trim(),
    description_md: d.description_md.trim() || null,
    kind: d.kind,
    url: d.url.trim() || null,
    responsible: d.responsible,
    evidence: d.evidence,
    due_days: parseDueDays(d.due_days) ?? ONBOARDING_ITEM_DEFAULTS.due_days,
    course_id: COURSE_LINK_KINDS.includes(d.kind) ? d.course_id : null,
    requires_verification: d.requires_verification,
  };
  return d.id ? { id: d.id, ...input } : input;
}

/** A fingerprint of the list for "unsaved changes" (the fields and the order, not the keys). */
export function listFingerprint(list: DraftItem[]): string {
  return JSON.stringify(list.map(({ key, ...rest }) => rest));
}

/** Move the item at `index` by `delta` places (the move up and move down buttons). */
export function moveItem<T>(list: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) return list;
  const next = list.slice();
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved);
  return next;
}
